'use client'
import { useMemo, type FC } from 'react'
import styled from 'styled-components'
import { designGrid, mq, vw, font12, font15 } from '@/styles'
import { Link } from '../base/Link'
import { SliderFlickity } from '../base/SliderFlickity'
import { AnimatePresence, motion } from 'motion/react'
import { TypeCmsHomepage } from '@/schema'
import { Img } from '../base/Img'
import { useDeviceType } from '@/hooks'



type Props = {
	data: TypeCmsHomepage['header']
}

const getImages = (media = []) => {
  return media.map((item, index) => {
    return (
      <motion.div key={index} >
        <Img media={item?.image}/>
      </motion.div>
    )
  })
}

export const HeaderHome: FC<Props> = ({ data }) => {
  const { text, linkText, link, slider } = data
  const Images = useMemo(() => getImages(slider), [slider])
  const { isMobile } = useDeviceType()

  return (
    <AnimatePresence>
      <Header initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .9, duration: .5 }}>
        <SliderFlickity className={'slider__images fade'} hasCustomDots={true} options={{
          contain: false,
          wrapAround: true,
          initialIndex: 0,
          freeScroll: false,
          cellAlign: 'center',
          draggable: isMobile,
          accessibility: true,
          autoPlay: 4000,
          pauseAutoPlayOnHover: false
        }}>
          {Images}
        </SliderFlickity>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <h1>{text} <Link href={link?.href}>{linkText}<img src='/images/svg/i--arrow-right.svg' alt='Icon arrow right' /></Link></h1>
      </Header>
    </AnimatePresence>
  )
}

const Header = styled(motion.header)`
  ${designGrid({})}
  margin: 0 0 ${vw(80, 'mobile')};
  overflow: hidden;

  ${mq.greaterThan('tablet')} {
    margin: 0 0 ${vw(80, 'desktop')};
  }

  .slider-wrapper {
    position: relative;
  }

  > div {
    grid-column: 1 / span 6;
    overflow: hidden;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }

    &,
    figure,
    picture {
      height: ${vw(475, 'mobile')};

      ${mq.greaterThan('tablet')} {
        height: ${vw(650, 'desktop')};
      }
    }

    .flickity-cell {
      width: ${vw(370, 'mobile')};

      ${mq.greaterThan('tablet')} {
        width: ${vw(1390, 'desktop')};
      }
    }

    .flickity-page-dots {
      display: none;
    }

    .custom-dots {
      bottom: ${vw(20, 'mobile')};
      display: flex;
      gap: 5px;
      justify-content:center;
      margin: 0 auto;
      max-width: 640px;
      position: absolute;
      padding: 0 20px;
      right: 50%;
      transform: translateX(50%);
      width: 100%;
      z-index: 2;

      ${mq.greaterThan('tablet')} {
        gap: 10px;
        bottom: ${vw(30, 'desktop')};
      }
    }

  }

  > h1 {
    ${font15(true)}
    grid-column: 1 / span 6;
    margin-top: ${vw(30, 'mobile')};
    padding: 0 ;
    text-align: center;

    ${mq.greaterThan('tablet')} {
      ${font15(false)}
      grid-column: 3 / span 8;
      margin-top: ${vw(80, 'desktop')};
      padding: 0 ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      font-size: 15px;
    }
  }

  a {
    ${font12(true)}
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 20px auto 0;
    position: relative;
    text-align: center;
    text-transform: uppercase;
    top: 0;

    ${mq.greaterThan('tablet')} {
      ${font12(false)}
    }

    &:hover {
      img {
        transform: translateX(5px);
      }
    }

    img {
      margin-left: 5px;
      transition: 300ms transform linear;
      width: 10px;
    }
  }
`

